import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { UserLookup } from "@/hooks/use-users";

/**
 * Monday's CDN (files.monday.com) sender ikke CORS-headere, så hot-linking
 * derfra spammer konsollen og er upålitelig på sikt. Behandle dem som
 * manglende avatar og fall tilbake til initialer. Migrasjon til Supabase
 * Storage er fortsatt aktuell, men dette stopper støyen umiddelbart.
 */
function isUsableAvatarUrl(url: string | null | undefined): url is string {
  if (!url) return false;
  return !/(?:^|\/\/)files\.monday\.com\b/.test(url);
}

export function Avatar({ user, size = 28 }: { user: UserLookup | undefined; size?: number }) {
  if (!user) return <span className="inline-block rounded-full bg-gray-200" style={{ width: size, height: size }} />;
  const initials = user.full_name.split(" ").map(n => n[0]).join("").slice(0, 2);
  const usable = isUsableAvatarUrl(user.avatar_url);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {usable ? (
          <img src={user.avatar_url!} alt={user.full_name} className="rounded-full object-cover shrink-0 ring-2 ring-white" style={{ width: size, height: size }} />
        ) : (
          <span className="inline-flex items-center justify-center rounded-full bg-primary/10 text-primary text-[11px] font-medium shrink-0 ring-2 ring-white" style={{ width: size, height: size }}>{initials}</span>
        )}
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs font-medium">{user.full_name}</TooltipContent>
    </Tooltip>
  );
}

export function AvatarGroup({ userIds, userMap }: { userIds: string[]; userMap: Map<string, UserLookup> }) {
  if (userIds.length === 0) return <span className="text-[11px] text-gray-300">—</span>;
  return (
    <div className="flex justify-center -space-x-2">
      {userIds.map(id => <Avatar key={id} user={userMap.get(id)} size={28} />)}
    </div>
  );
}
